import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Home, CheckCircle, ArrowRight, Bath, Car, ChefHat, Sofa } from 'lucide-react';

const ResidentialPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.35 }
  };

  const residentialBenefits = [
    'Elegant, modern aesthetic',
    'Hypoallergenic surface',
    'Easy to clean',
    'Increases property value',
    'Sustainable and eco-friendly',
    'Customizable finishes'
  ];

  const residentialSpaces = [
    { icon: Sofa, title: 'Living Areas', description: 'Open plan living rooms, family rooms, and entertainment spaces' },
    { icon: ChefHat, title: 'Kitchen & Dining', description: 'Modern kitchens and dining areas that blend style with functionality' },
    { icon: Bath, title: 'Bathrooms', description: 'Spa-like bathroom environments with slip-resistant options' },
    { icon: Car, title: 'Garages & Utility', description: 'Garage floors, laundry rooms, and utility areas' }
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="residential-page">
      {/* Hero Video Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="residential-hero-video"
        >
          <source src="https://customer-assets.emergentagent.com/job_concretepro/artifacts/0nlu9qb3_20240327_173503.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="max-w-4xl"
          >
            <Home className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              Residential
              <span className="bg-gradient-to-r from-slate-300 to-slate-100 bg-clip-text text-transparent block">
                Solutions
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              Transform your home with elegant, durable polished concrete that combines beauty 
              with practicality for modern living.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-slate-500 to-slate-700 hover:from-slate-600 hover:to-slate-800 text-white border-0 shadow-2xl"
                data-testid="hero-cta-button"
              >
                Get Home Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Residential Spaces */}
      <section className="section-spacing bg-background">
        <div className="container mx-auto px-4 py-16">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-800 dark:text-slate-100">
              Perfect for Every Room
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              From luxury living areas to practical utility spaces, polished concrete adapts 
              beautifully to residential environments.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {residentialSpaces.map((space, index) => (
              <motion.div
                key={space.title}
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-6 text-center">
                    <space.icon className="w-12 h-12 text-slate-600 dark:text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2 text-slate-800 dark:text-slate-100">
                      {space.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {space.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Residential Benefits */}
      <section className="section-spacing bg-slate-50 dark:bg-slate-900" data-testid="residential-benefits">
        <div className="container mx-auto px-4 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <div className="relative">
                <Card className="overflow-hidden shadow-lg">
                  <div className="relative aspect-video">
                    <iframe
                      src="https://www.youtube.com/embed/EkepD-EIPFk?hd=1&vq=hd1080&rel=0&modestbranding=1"
                      title="Residential Polished Concrete Showcase"
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      data-testid="residential-showcase-video"
                    />
                  </div>
                  <CardContent className="p-4 text-center bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700">
                    <h3 className="text-lg font-semibold mb-2 text-slate-800 dark:text-slate-100">
                      Luxury Residential Installation
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      Ocean view apartment transformation with premium polished concrete
                    </p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>

            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-800 dark:text-slate-100">
                Why Homeowners Choose Polished Concrete
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
                Modern homeowners are discovering the benefits of polished concrete for its combination of 
                contemporary aesthetics, practical benefits, and long-term value. It's the perfect choice 
                for those who want a sophisticated, low-maintenance flooring solution.
              </p>
              <div className="grid gap-4">
                {residentialBenefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-200 font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Residential Project Video */}
      <section className="bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 py-8">
          <motion.div {...fadeInUp} className="text-center mb-6">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-800 dark:text-slate-100">
              Residential Project Showcase
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Watch another stunning residential transformation showcasing our polished concrete expertise
            </p>
          </motion.div>
          
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
            <Card className="overflow-hidden shadow-lg">
              <div className="relative aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/hPxEF7U1qV0?hd=1&vq=hd1080&rel=0&modestbranding=1"
                  title="Residential Polished Concrete Project"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="residential-additional-video"
                />
              </div>
              <CardContent className="p-6 text-center bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700">
                <h3 className="text-xl font-semibold mb-2 text-slate-800 dark:text-slate-100">
                  Residential Polished Concrete Installation
                </h3>
                <p className="text-slate-600 dark:text-slate-300">
                  See our professional residential installation process and stunning results
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Process for Residential */}
      <section className="section-spacing bg-background">
        <div className="container mx-auto px-4 py-16 text-center">
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-800 dark:text-slate-100">
              Residential Process
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              We understand that working in homes requires special consideration for cleanliness, 
              noise levels, and family schedules.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div {...fadeInUp} transition={{ delay: 0.1 }}>
              <Card className="p-6 h-full">
                <div className="text-center">
                  <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-xl font-bold text-slate-600 dark:text-slate-300">1</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-slate-800 dark:text-slate-100">Consultation</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    In-home assessment and design consultation to understand your vision and requirements
                  </p>
                </div>
              </Card>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.2 }}>
              <Card className="p-6 h-full">
                <div className="text-center">
                  <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-xl font-bold text-slate-600 dark:text-slate-300">2</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-slate-800 dark:text-slate-100">Preparation</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Careful preparation with minimal disruption to your daily routine and living spaces
                  </p>
                </div>
              </Card>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.3 }}>
              <Card className="p-6 h-full">
                <div className="text-center">
                  <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-xl font-bold text-slate-600 dark:text-slate-300">3</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-slate-800 dark:text-slate-100">Transformation</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Professional installation with dust control and respect for your home environment
                  </p>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 py-16 text-center">
          <motion.div {...fadeInUp}>
            <Card className="p-12 bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-100 dark:to-slate-200 text-white dark:text-slate-900">
              <Home className="w-16 h-16 mx-auto mb-6 text-slate-300 dark:text-slate-600" />
              <h2 className="text-3xl font-bold mb-4">
                Transform Your Home Today
              </h2>
              <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
                Discover how polished concrete can enhance your living spaces while adding 
                value to your property investment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-slate-500 to-slate-700 hover:from-slate-600 hover:to-slate-800 text-white border-0"
                    data-testid="residential-cta-contact"
                  >
                    Get Home Quote <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    variant="outline"
                    size="lg"
                    className="border-white text-white hover:bg-white hover:text-slate-900 dark:border-slate-900 dark:text-slate-900 dark:hover:bg-slate-900 dark:hover:text-white"
                    data-testid="residential-cta-services"
                  >
                    View All Services
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResidentialPage;